import Typography from '@material-ui/core/Typography'
import { stores, strings } from '@stores'
import { useObserver } from 'mobx-react-lite'
import React from 'react'
import * as css from './BulkOperationTabContent.css'
import { useTranslation } from 'react-i18next'
import { LeftSideBulkOperationTabContent } from './LeftSideBulkOperationTabContent'
import { RightSideBulkOperationTabContent } from './RightSideBulkOperationTabContent'

interface IBulkOperationTabContentProps {
  isLoading: boolean
  allChannels: any[]
  allDrops: any[]
  retailFreeTags: any[]
  wholesaleFreeTags: any[]
  wholesaleClusters: any[]
  retailClusters: any[]
  salesPeriods: any[]
  clientExclusions: any[]
  zoneExclusions: any[]
  wholesaleClusterExclusions: any[]
  retailClusterExclusions: any[]
}

export const BulkOperationTabContent: React.FunctionComponent<
  IBulkOperationTabContentProps
> = ({
  isLoading,
  wholesaleClusters,
  retailClusters,
  wholesaleFreeTags,
  retailFreeTags,
  allChannels,
  allDrops,
  salesPeriods,
  clientExclusions,
  zoneExclusions,
  wholesaleClusterExclusions,
  retailClusterExclusions
}) => {
  return useObserver(function useHook() {
    const {
      wsBuyingSessionProduct: {
        bulkOpSelectedTab,
        bulkOpSelectedEntities,
        addBulkOpSelectedEntity,
        removeBulkOpSelectedEntity,
        removeBulkOpEntity,
        getBulkOperationWarnings,
        handleTagClick,
        selectedChipsForDrag,
        rightSideSelectedChipsForDrag,
        mouseDownHandlerForChips,
        handleChipsSelectionByMouseDrag,
        handleChipsDeselection,
        onMouseUpHandlerOnChip,
        setIsWindowSelectionGoingOn,
        onSelectAllTags
      }
    } = stores
    const { t } = useTranslation()
    let entities = []
    let draggableSectionTitle = ''
    const warnings = getBulkOperationWarnings(bulkOpSelectedTab, t)
    if (!bulkOpSelectedTab || bulkOpSelectedTab === strings.tagTypes.wholesaleCluster) {
      entities = wholesaleClusters
      draggableSectionTitle = t(
        'bb.common.bulkOpDragSectionHeaders.draggableSectionHeaders.wholesaleClusters',
        'All wholesale clusters'
      )
    } else if (
      !bulkOpSelectedTab ||
      bulkOpSelectedTab === strings.tagTypes.retailCluster
    ) {
      entities = retailClusters
      draggableSectionTitle = t(
        'bb.common.bulkOpDragSectionHeaders.draggableSectionHeaders.retailClusters',
        'All retail clusters'
      )
    } else if (bulkOpSelectedTab === strings.tagTypes.channel) {
      entities = allChannels
      draggableSectionTitle = t(
        'bb.common.bulkOpDragSectionHeaders.draggableSectionHeaders.channels',
        'All channels tags'
      )
    } else if (bulkOpSelectedTab === strings.tagTypes.drop) {
      entities = allDrops
      draggableSectionTitle = t(
        'bb.common.bulkOpDragSectionHeaders.draggableSectionHeaders.drops',
        'All drops'
      )
    } else if (bulkOpSelectedTab === strings.tagTypes.retailFreeTags) {
      entities = retailFreeTags
      draggableSectionTitle = t(
        'bb.common.bulkOpDragSectionHeaders.draggableSectionHeaders.retailFreeTags',
        'All retail free tags'
      )
    } else if (bulkOpSelectedTab === strings.tagTypes.wholesaleFreeTags) {
      entities = wholesaleFreeTags
      draggableSectionTitle = t(
        'bb.common.bulkOpDragSectionHeaders.draggableSectionHeaders.wholesaleFreeTags',
        'All wholesale free tags'
      )
    } else if (bulkOpSelectedTab === strings.tagTypes.salesPeriod) {
      entities = salesPeriods
      draggableSectionTitle = t(
        'bb.common.bulkOpDragSectionHeaders.draggableSectionHeaders.salesPeriod',
        'All Sales Period'
      )
    } else if (bulkOpSelectedTab === strings.tagTypes.clientExclusions) {
      entities = clientExclusions
      draggableSectionTitle = t(
        'bb.common.bulkOpDragSectionHeaders.draggableSectionHeaders.clientExclusions',
        'All clients'
      )
    } else if (bulkOpSelectedTab === strings.tagTypes.zoneExclusions) {
      entities = zoneExclusions
      draggableSectionTitle = t(
        'bb.common.bulkOpDragSectionHeaders.draggableSectionHeaders.zoneExclusions',
        'All zones'
      )
    } else if (bulkOpSelectedTab === strings.tagTypes.wholesaleClusterExclusions) {
      entities = wholesaleClusterExclusions
      draggableSectionTitle = t(
        'bb.common.bulkOpDragSectionHeaders.draggableSectionHeaders.wholesaleClusterExclusions',
        'All wholesale clusters'
      )
    } else if (bulkOpSelectedTab === strings.tagTypes.retailClusterExclusions) {
      entities = retailClusterExclusions
      draggableSectionTitle = t(
        'bb.common.bulkOpDragSectionHeaders.draggableSectionHeaders.retailClusterExclusions',
        'All retail clusters'
      )
    }
    const unSelectedEntities = entities?.filter(
      currentEntity =>
        !bulkOpSelectedEntities?.[bulkOpSelectedTab]?.find(
          selectedEntity =>
            (selectedEntity.Value && selectedEntity.Value === currentEntity.Value) ||
            (selectedEntity.Id && selectedEntity.Id === currentEntity.Id)
        )
    )
    const selectedEntities = bulkOpSelectedEntities?.[bulkOpSelectedTab]

    return (
      <div className={css.tabContent}>
        <div className={css.bulkOpertaionWarnings}>
          {warnings.map((warning, index) => (
            <Typography key={`warning_${index}`} className={css.bulkOpertaionWarning}>
              {warning}
            </Typography>
          ))}
        </div>
        <div className={css.entityContainer}>
          <LeftSideBulkOperationTabContent
            selectedChipsForDrag={selectedChipsForDrag}
            unSelectedEntities={unSelectedEntities}
            draggableSectionTitle={draggableSectionTitle}
            bulkOpSelectedTab={bulkOpSelectedTab}
            handleChipsDeselection={handleChipsDeselection}
            handleChipsSelectionByMouseDrag={handleChipsSelectionByMouseDrag}
            setIsWindowSelectionGoingOn={setIsWindowSelectionGoingOn}
            mouseDownHandlerForChips={mouseDownHandlerForChips}
            removeBulkOpSelectedEntity={removeBulkOpSelectedEntity}
            onMouseUpHandlerOnChip={onMouseUpHandlerOnChip}
            onSelectAllTags={onSelectAllTags}
          />
          <RightSideBulkOperationTabContent
            rightSideSelectedChipsForDrag={rightSideSelectedChipsForDrag}
            selectedEntities={selectedEntities}
            bulkOpSelectedTab={bulkOpSelectedTab}
            handleChipsDeselection={handleChipsDeselection}
            handleChipsSelectionByMouseDrag={handleChipsSelectionByMouseDrag}
            setIsWindowSelectionGoingOn={setIsWindowSelectionGoingOn}
            mouseDownHandlerForChips={mouseDownHandlerForChips}
            onMouseUpHandlerOnChip={onMouseUpHandlerOnChip}
            addBulkOpSelectedEntity={addBulkOpSelectedEntity}
            removeBulkOpEntity={removeBulkOpEntity}
            handleTagClick={handleTagClick}
            onSelectAllTags={onSelectAllTags}
          />
        </div>
      </div>
    )
  })
}
